import React, {useContext, useEffect, useMemo, useRef, useState} from 'react'
import _ from 'lodash'
// import * as Y from 'yjs';
// import { IndexeddbPersistence } from 'y-indexeddb';
// import { WebrtcProvider } from 'y-webrtc';
// import { useParams } from 'react-router';
import {Helmet} from 'react-helmet'
import {Checkbox, Dimmer, Dropdown, Icon, Loader, Popup} from 'semantic-ui-react'
import {useGesture} from '@use-gesture/react'
import {useGet} from 'restful-react'
import {SlLoop} from 'react-icons/sl'
import {RxAlignLeft, RxAlignRight} from 'react-icons/rx'
import {IParsedMediaItem} from '@/utils/media'
import {
  ILabelingWidgetConfig,
  labelingWidgetMap,
  IEvent,
  ILabel,
  IUserLabelsMap,
  LabelingContext,
  parseEvent,
  useSyncedLabelTracks,
  LabelingContextWrapper,
} from '@/features/labeling'
import {
  IMixerSidebarRef,
  Sidebar as MixerSidebar,
  SemanticFilteringItemType,
} from '@/features/mixer'
import {Split} from '@/features/split'
import {
  IExpressionConfig,
  evalExpression,
  expressionConfigParamValues,
} from '@/features/expressions'
import {IDataSourceItem, LegacyCatalogContext, useDataSourceStats} from '@/features/data-sources'
import {VideolabConfigWrapper} from '../../contexts/config'
import {IVideoPlayerProps, VideoPlayer} from '../../components/video'
import {IUpNextConfig, UpNext} from '../../components/up-next'
import {VideoControls} from '../../components/video/controls'
import {Sidebar, SidebarComponents} from '../../components/sidebar'
import {Timeline} from '../../components/timeline'
import {useConfig} from '../..'
import {staticChannelTypeDescriptions} from './const'
import {VideoRegions} from '../../components/video-overlay'
import {CommandModal, ICommandModalRef} from '../../components/command-modal'
import './style.css'

export interface IPlaybackMode {
  description: string
  buttonContent: React.ReactNode
  videoProps?: Partial<IVideoPlayerProps>
}

export interface IPlaybackModeMap {
  pauseAtBlockStart: IPlaybackMode
  loopSequence: IPlaybackMode
  pauseAtBlockEnd: IPlaybackMode
}

function RouteVideoLabInternal() {
  const commandModalRef = useRef<ICommandModalRef>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const catalogSidebarRef = useRef<IMixerSidebarRef<SemanticFilteringItemType>>(null)

  const {
    apiCatalogName,
    catalog,
    catalogDataSource,
    staticCategoricalsVocabulary,
    ensureFetchCatalog,
    catalogStats,
  } = useContext(LegacyCatalogContext)

  const {getCrdtLabelTrackCollection} = useContext(LabelingContext)

  const {formatTime, isLabelingDisabled, updateConfig} = useConfig()

  const playbackModes: IPlaybackModeMap = {
    pauseAtBlockStart: {
      description: 'pause at selection start (1)',
      buttonContent: <RxAlignLeft />,
      videoProps: {
        pauseAtRangeStart: true,
      },
    },
    loopSequence: {
      description: 'loop selection (2)',
      buttonContent: <SlLoop />,
      videoProps: {
        clampToRange: true,
        loop: true,
        controls: false,
      },
    },
    pauseAtBlockEnd: {
      description: 'pause at selection end (3)',
      buttonContent: <RxAlignRight />,
      videoProps: {
        pauseAtRangeEnd: true,
      },
    },
  }

  const supportedPlaybackRates = [0.25, 0.5, 1, 2, 3]

  const [playbackRate, setPlaybackRate] = useState(1.0)
  const [currentUserLabels, setCurrentUserLabels] = useState<IUserLabelsMap>()
  const [videoUrl, setVideoUrl] = useState<string>()
  const [videoDuration, setVideoDuration] = useState<number>()
  const [videoRange, setVideoRange] = useState<[number, number]>()
  const [currentEvent, setCurrentEvent] = useState<IEvent>()
  const [currentTime, setCurrentTime] = useState<number>()
  const [seekOffset, setSeekOffset] = useState<number>(0)
  const [selectedItem, setSelectedItem] = useState<IParsedMediaItem>()
  const [activePlaybackModes, setActivePlaybackModes] = useState<(keyof IPlaybackModeMap)[]>()
  const [currentApiErrors, setCurrentApiErrors] = useState<string[]>()
  const [currentPredictions, setCurrentPredictions] = useState<IParsedMediaItem['predictions']>([])
  const [currentPlots, setCurrentPlots] = useState<IParsedMediaItem['plots']>()
  const [filteredPredictions, setFilteredPredictions] = useState<IEvent[]>([])
  const [userChannels, setUserChannels] = useState<string[]>()
  const [channelLock, setChannelLock] = useState<string[]>()
  const [predictionFilterExpression, setPredictionFilterExpression] = useState<IExpressionConfig>({
    expression: '',
    params: {},
  })
  const [showUpNext, setShowUpNext] = useState(true)
  const [upNextConfig, setUpNextConfig] = useState<IUpNextConfig>({
    includeLabels: true,
  })
  const [autoPlay, setAutoPlay] = useState(false)
  const [predictionTypeOptions, setPredictionTypeOptions] = useState<any[]>()

  // TODO: make less confusing
  const [compundItem, setCompundItem] = useState<IParsedMediaItem | undefined>()

  const {
    // data: similarList,
    // loading: isVideoUrlLoading,
    refetch: refetchVideoUrl,
    cancel: cancelRefetchVideoUrl,
  } = useGet({
    lazy: true,
    path: ({catalog, vionlabs_id}: {catalog?: string; vionlabs_id: string}) =>
      `/api/upload/catalogs/${catalog || apiCatalogName}/vionlabs_id/${vionlabs_id}/download_url`,
    resolve: (data) => setVideoUrl(_.get(data, 'download_signed_url_v4')),
  })

  const {
    loading: isPredictionsLoading,
    refetch: refetchPredictions,
    cancel: cancelRefetchPredictions,
  } = useGet({
    lazy: true,
    path: ({catalog, vionlabs_id}: {catalog?: string; vionlabs_id: string}) =>
      `/api/labels/catalogs/${catalog || apiCatalogName}/aggregated-results/vionlabs_id/${vionlabs_id}`,
    resolve: (data) => {
      setCurrentPredictions(
        _.chain(sortPredictions(_.get(data, 'predictions')))
          .map(parseEvent)
          .value()
      )
      setCurrentApiErrors(_.get(data, 'errors'))
      setCurrentPlots(_.get(data, 'plots'))
    },
  })

  // TODO: this is needed to exist for upstream sync to happen, values should be used
  const {isBusy: isLabelsLoading, canLabel} = useSyncedLabelTracks(
    catalogDataSource === 'api' ? apiCatalogName : selectedItem?.catalog,
    selectedItem?.vionlabs_id
  )

  const _mergedItems = useMemo(
    () =>
      _.chain(catalog)
        .map((v) => {
          return _.filter(
            _.get(v, 'predictions') || [],
            (p) => p && p['type'] === 'categoricals'
          ) as Omit<IDataSourceItem, '_id'>
        })
        .flatten()
        .concat(currentPredictions || [])
        .concat(staticCategoricalsVocabulary ? [staticCategoricalsVocabulary] : [])
        .value(),
    [catalog, currentPredictions, staticCategoricalsVocabulary]
  )

  const predictionStats = useDataSourceStats(_mergedItems)

  const dataFieldStats = useMemo(
    () => _.merge({categories: _.cloneDeep(catalogStats.categories)}, predictionStats),
    [catalogStats, predictionStats]
  )

  useEffect(ensureFetchCatalog)

  useEffect(() => {
    if (!predictionStats) return
    const types = _.chain(predictionStats.types).keys().orderBy().value()
    setPredictionTypeOptions(
      _.chain(types)
        .map((t) => ({text: t, value: t}))
        .value()
    )
    if (upNextConfig.types && _.isEqual(upNextConfig.types, types)) return
    setUpNextConfig((prev) => ({
      ...prev,
      types,
    }))
  }, [predictionStats])

  useEffect(() => {
    resetVideo()
    setCurrentUserLabels(undefined)
    setCurrentEvent(undefined)
    setCompundItem(undefined)
    setCurrentPredictions([])
    setCurrentPlots(undefined)

    if (!selectedItem) return
    const {catalog: itemCatalogName, vionlabs_id, predictions, plots} = selectedItem || {}
    if (!vionlabs_id) return

    refetchVideoUrl({
      pathParams: {
        catalog: itemCatalogName,
        vionlabs_id,
      },
    })

    if (catalogDataSource === 'api') {
      refetchPredictions({
        pathParams: {
          catalog: apiCatalogName,
          vionlabs_id,
        },
      })
    } else {
      if (predictions)
        setCurrentPredictions(_.chain(sortPredictions(predictions)).map(parseEvent).value())
      if (plots) setCurrentPlots(plots)
    }

    // colab sync stuff
    const labels =
      selectedItem.vionlabs_id ? getCrdtLabelTrackCollection(selectedItem.vionlabs_id) : undefined
    if (!labels) return
    setCurrentUserLabels(labels.toJSON())

    labels.observeDeep(handleCrdtLabelTrackCollectionChanged)
    return () => {
      labels.unobserveDeep(handleCrdtLabelTrackCollectionChanged)
      cancelRefetchVideoUrl()
      cancelRefetchPredictions()
    }
  }, [selectedItem, apiCatalogName])

  useEffect(handleUpdateTime, [currentEvent])

  useEffect(() => {
    if (
      !(
        predictionFilterExpression &&
        predictionFilterExpression.expression &&
        predictionFilterExpression.expression.length > 0
      )
    ) {
      setFilteredPredictions(currentPredictions || [])
      return
    }
    // const itemValues = (
    //     _.chain(selectedItem)
    //     .toPairs()
    //     .filter(([k]) => {
    //         if (k === 'predictions') return false;
    //         if (k === 'plots') return false;
    //         return true;
    //     })
    //     .fromPairs()
    //     .value()
    // );
    setFilteredPredictions(
      _.chain(currentPredictions)
        .filter((p) => {
          const {result} = evalExpression(predictionFilterExpression.expression, {
            duration: videoDuration,
            // ...itemValues,
            ...expressionConfigParamValues(predictionFilterExpression.params),
            ...p,
          })
          // TODO: handle/display expression errors
          return result
        })
        .value()
    )
  }, [currentPredictions, predictionFilterExpression])

  useEffect(() => {
    setCompundItem(
      selectedItem ?
        {
          ...selectedItem,
          predictions: filteredPredictions,
          plots: currentPlots,
        }
      : undefined
    )
  }, [selectedItem, filteredPredictions, currentPlots])

  useEffect(() => {
    if (!(channelLock && channelLock.length > 0)) return
    if (!currentEvent) return
    if (currentEvent.type === channelLock[0]) return
    setCurrentEvent((prev) => {
      if (!prev) return
      return {
        ...prev,
        // TODO: lock multiple channels?
        type: channelLock[0],
      }
    })
  }, [channelLock])

  useGesture(
    {
      onKeyDown: (e) => handleOnKeyDown(e.event),
    },
    {
      target: window,
      eventOptions: {
        passive: false,
      },
    }
  )

  function sortPredictions(events: IEvent[]): IEvent[] {
    const res = _.chain(events)
      .map((e) => {
        if (e.type === undefined) return
        if (e.start === undefined) return {...e, start: 0}
        return e
      })
      .compact()
      .orderBy(['type', 'start', 'end'])
      .value()
    return res
  }

  function handleOnKeyDown(e: KeyboardEvent) {
    const target = e.target as HTMLElement | null
    if (!target) return
    if (!videoRef.current) return
    if (!_.includes(['BODY', 'A'], target.tagName)) return
    let handled = false
    let t = videoRef.current.currentTime || 0
    let diff = 1
    const fps = selectedItem?.fps || 25 // default "best guess"
    if ((e.ctrlKey && e.shiftKey) || e.altKey) {
      // TODO: fps from video data?
      const tFrame = 1 / fps
      diff = tFrame
    } else if (e.shiftKey) diff = 10
    const numKey = _.toNumber(e.key) || undefined
    switch (numKey || e.code) {
      case 'ArrowLeft':
        t -= diff
        handled = true
        break
      case 'ArrowRight':
        t += diff
        handled = true
        break
      case 'Space':
        try {
          if (videoRef.current.paused) videoRef.current.play()
          else videoRef.current.pause()
        } catch (_error) {
          /* empty */
        }
        e.stopPropagation()
        e.preventDefault()
        return
      case 'KeyW':
        if (!currentEvent) {
          selectFirstPrediction()
          break
        }
        selectTrackAbove(currentEvent)
        break
      case 'KeyS':
        if (!currentEvent) {
          selectFirstPrediction()
          break
        }
        selectTrackBelow(currentEvent)
        break
      case 'KeyA':
        if (!currentEvent) {
          selectFirstPrediction()
          break
        }
        selectPreviousPredictionOrLabel(currentEvent)
        break
      case 'KeyD':
        if (!currentEvent) {
          selectFirstPrediction()
          break
        }
        selectNextPredictionOrLabel(currentEvent)
        break
      case 'KeyR':
        selectRandomPrediction()
        break
      case 'Comma': {
        const r = supportedPlaybackRates[supportedPlaybackRates.indexOf(playbackRate) - 1]
        if (!r) break
        setPlaybackRate(r)
        break
      }
      case 'Period': {
        const r = supportedPlaybackRates[supportedPlaybackRates.indexOf(playbackRate) + 1]
        if (!r) break
        setPlaybackRate(r)
        break
      }
      case 1:
        togglePlaybackMode('pauseAtBlockStart')
        break
      case 2:
        togglePlaybackMode('loopSequence')
        break
      case 3:
        togglePlaybackMode('pauseAtBlockEnd')
        break
    }
    if (handled) {
      // try to snap seek time to nearest fps fraction
      if (t !== Math.floor(t) && fps) {
        // fractional component
        const f = t - Math.floor(t)
        t = Math.floor(t) + Math.round(f * fps) / fps
      }
      videoRef.current.currentTime = t
      e.stopPropagation()
    }
  }

  function handleCrdtLabelTrackCollectionChanged() {
    const vionlabs_id = _.get(selectedItem, 'vionlabs_id')
    if (!vionlabs_id) return
    const col = getCrdtLabelTrackCollection(vionlabs_id)
    if (!col) return
    setCurrentUserLabels(col.toJSON())
    // if (!crdtLabels) return;
    // setCurrentUserLabels(crdtLabels.toJSON());
    // if (vionlabs_id) syncUpstreamLabels(vionlabs_id);
  }

  function resetVideo() {
    setVideoUrl(undefined)
    setCurrentEvent(undefined)
    setVideoDuration(undefined)
    if (!videoRef.current) return
    videoRef.current.removeAttribute('src')
    videoRef.current.load()
  }

  function handleEventClick(event: IEvent) {
    if (event === currentEvent) {
      handleUpdateTime(event)
      return
    }
    if (channelLock && channelLock.length > 0) {
      // TODO: lock multiple channels?
      setCurrentEvent({
        ...event,
        type: channelLock[0],
      })
      return
    }
    setCurrentEvent(event)
  }

  function handleOnTimeDoubleClick() {
    commandModalRef.current?.open()
  }

  function handleUpdateTime(event: IEvent | undefined = currentEvent) {
    if (!event) {
      setVideoRange(undefined)
      return
    }
    const isValid = event.start !== -1 && event.end !== -1
    if (!isValid) return
    const r: [number, number] = [event.start, event.end || event.start + 5]
    setCurrentTime(r[0])
    setVideoRange(r)
  }

  function togglePlaybackMode(key: keyof IPlaybackModeMap) {
    if (_.includes(activePlaybackModes, key)) setActivePlaybackModes((prev) => _.without(prev, key))
    else setActivePlaybackModes((prev) => [...(prev || []), key])
  }

  function findPredictions(
    type: string,
    f: (event: IEvent) => boolean,
    applyFilters = false
  ): IEvent[] {
    return _.chain(applyFilters ? filteredPredictions : currentPredictions)
      .filter((p) => {
        if (p.hidden) return false
        if (p.start === undefined) return false
        if (!p.type) return false
        if (p.type.toLowerCase() !== type.toLowerCase()) return false
        return f(p)
      })
      .value()
  }

  function findLabels(type: string, f: (label: ILabel<object>) => boolean): ILabel<object>[] {
    return _.chain(currentUserLabels)
      .get(type.toLowerCase())
      .values()
      .filter((l) => {
        return f(l)
      })
      .value()
  }

  function findPredictionFromLabel(label: ILabel<object>): IEvent | undefined {
    if (!label.type) return
    const res = findPredictions(label.type, (p) => {
      if (p.start === label.start && p.end === label.end) return true
      return false
    })
    // TODO: possibly multiple matches?
    if (res.length > 1) {
      console.error('Multiple predictions found for label', label, res)
    }
    return _.first(res)
  }

  function findLabelFromPrediction(event: IEvent): ILabel<object> | undefined {
    if (!event.type) return
    const res: ILabel<object> | undefined = _.chain(currentUserLabels)
      .get(event.type.toLowerCase())
      .get(event.start)
      .value()
    if (!res) return
    if (res.end !== event.end) return
    return res
  }

  function currentChannelTypes() {
    return _.chain(filteredPredictions)
      .groupBy((v) => v.type)
      .toPairs()
      .concat(_.map(userChannels, (t) => [t, []] as [string, IEvent[]]))
      .map(([k]) => k)
      .uniq()
      .value()
  }

  function selectTrackAbove(event: IEvent) {
    if (!event.type) return
    const channels = currentChannelTypes()
    const t = channels[channels.indexOf(event.type) - 1]
    if (!t) return
    setCurrentEvent({
      ...event,
      type: t,
    })
  }

  function selectTrackBelow(event: IEvent) {
    if (!event.type) return
    const channels = currentChannelTypes()
    const t = channels[channels.indexOf(event.type) + 1]
    if (!t) return
    setCurrentEvent({
      ...event,
      type: t,
    })
  }

  function selectPreviousPredictionOrLabel(event: IEvent) {
    if (!event.type) return
    const l = findLabels(
      event.type,
      (l) => l.start < event.start || (l.end || 0) < (event.end || 0)
    ).pop()
    const p = findPredictions(
      event.type,
      (e) => e.start < event.start || (e.end || 0) < (event.end || 0),
      true
    ).pop()
    const found = _.chain([l, p])
      .compact()
      .sortBy((v) => v?.start)
      .value()
      .pop()
    if (found) {
      setCurrentEvent(found)
    }
  }

  function selectNextPredictionOrLabel(event: IEvent) {
    if (!event.type) return
    const l = findLabels(
      event.type,
      (l) => l.start > event.start || (l.end || 0) > (event.end || 0)
    )[0]
    const p = findPredictions(
      event.type,
      (e) => e.start > event.start || (e.end || 0) > (event.end || 0),
      true
    )[0]
    const found = _.chain([l, p])
      .compact()
      .sortBy((v) => v?.start)
      .value()[0]
    if (found) {
      setCurrentEvent(found)
    }
  }

  function selectRandomPrediction() {
    const p = _.chain(filteredPredictions)
      .filter(
        (p) =>
          !p.hidden &&
          p.start !== undefined &&
          findLabelFromPrediction(p) === undefined &&
          !_.isEqual(p, currentEvent)
      )
      .sample()
      .value()
    if (!p) return
    setCurrentEvent(p)
  }

  function selectFirstPrediction() {
    const p = _.chain(filteredPredictions)
      .filter((p) => !p.hidden)
      .sortBy('start')
      .first()
      .value()
    setCurrentEvent(p)
  }

  function toggleChannelLock(key: string) {
    setChannelLock((prev) => {
      if (!prev) return [key]
      if (_.includes(prev, key)) return _.without(prev, key)
      // TODO: locking multiple channels?
      // return [...prev, key];
      return [key]
    })
  }

  function handleOnRunPredictionFilterExpression(expression: IExpressionConfig) {
    setPredictionFilterExpression(expression)
  }

  const _compoundVideoProps: IVideoPlayerProps =
    _.chain(activePlaybackModes)
      .map((k) => playbackModes[k].videoProps)
      .reduce((prev, curr) => ({
        ...prev,
        ...curr,
      }))
      .value() || undefined

  const selectedLabelingWidget: ILabelingWidgetConfig<any> =
    currentEvent && currentEvent.type && labelingWidgetMap[currentEvent.type.toLowerCase()] ?
      labelingWidgetMap[currentEvent.type.toLowerCase()]
    : labelingWidgetMap.default

  return (
    <div className='video-lab-route'>
      <Helmet>
        <title>Vionlabs - video lab</title>
      </Helmet>
      <Split
        className='main'
        direction='vertical'
        sizes={[80, 20]}
        snapOffset={10}
      >
        <div>
          <Split
            className='video-lab-content'
            sizes={[15, 60, 25]}
          >
            <MixerSidebar
              compact
              disableSetSelectedSeries
              ref={catalogSidebarRef}
              config={{
                mediaListViewMode: 'list',
              }}
              onSelectItem={(item) => {
                if (selectedItem?._id === item?._id) return
                setSelectedItem(item)
              }}
            />
            {/* <Sidebar className='left'>
                        <SidebarCatalog
                            ref={catalogSidebarRef}
                            onSelectedItemChanged={setSelectedItem}
                        />
                    </Sidebar> */}
            <VideoPlayer
              autoPlay={autoPlay}
              videoRef={videoRef}
              verifyCanPlay={false}
              videoUrl={videoUrl}
              seekOffset={seekOffset}
              range={videoRange}
              playbackRate={playbackRate}
              videoStyle={{
                width: '100%',
                height: '100%',
              }}
              onTimeUpdate={({time, duration}) => {
                setCurrentTime(time)
                if (!_.isEqual(duration, videoDuration)) setVideoDuration(duration)
              }}
              onFocus={(e) => e.target.blur()}
              overlay={
                <>
                  <div className='up-next-container'>
                    <div className='controls'>
                      <Checkbox
                        label='up next'
                        checked={showUpNext}
                        onChange={(_e, {checked}) => setShowUpNext(checked === true)}
                      />
                      <Popup
                        // inverted
                        openOnTriggerMouseEnter={false}
                        closeOnTriggerMouseLeave={false}
                        closeOnPortalMouseLeave={false}
                        position='bottom left'
                        trigger={
                          <button
                            disabled={!showUpNext}
                            children={<Icon name='configure' />}
                          />
                        }
                        content={
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: '0.25em',
                            }}
                          >
                            <Checkbox
                              label='include labels'
                              checked={upNextConfig.includeLabels}
                              onChange={(_e, {checked}) =>
                                setUpNextConfig((prev) => ({
                                  ...prev,
                                  includeLabels: checked === true,
                                }))
                              }
                            />
                            <div>
                              <div
                                style={{
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold',
                                }}
                              >
                                include types:
                              </div>
                              <Dropdown
                                selection
                                search
                                multiple
                                placeholder='include types'
                                options={predictionTypeOptions}
                                value={upNextConfig.types || []}
                                onChange={(_e, {value}) => {
                                  setUpNextConfig((prev) => ({
                                    ...prev,
                                    types: value as string[],
                                  }))
                                }}
                              />
                            </div>
                          </div>
                        }
                      />
                    </div>
                    {showUpNext ?
                      <>
                        <UpNext
                          data={filteredPredictions}
                          userLabels={currentUserLabels}
                          config={upNextConfig}
                          currentTime={currentTime}
                          onValueClick={handleEventClick}
                        />
                      </>
                    : null}
                  </div>
                  <VideoRegions
                    videoRef={videoRef}
                    currentTime={currentTime}
                    predictions={filteredPredictions}
                    onValueClick={handleEventClick}
                  />
                </>
              }
              {..._compoundVideoProps}
            />
            <Sidebar
              className='right'
              style={{position: 'relative'}}
            >
              <SidebarComponents
                predictionStats={dataFieldStats}
                labelingWidgetConfig={selectedLabelingWidget}
                data={compundItem}
                currentTime={currentTime}
                selectedEvent={currentEvent}
                userLabels={currentUserLabels}
                userRows={userChannels}
                isPredictionsLoading={isPredictionsLoading}
                isLabelsLoading={isLabelsLoading}
                isReadOnly={!canLabel || isLabelingDisabled}
                channelTypeDescriptions={staticChannelTypeDescriptions}
                apiErrors={currentApiErrors}
                onRunPredictionFilterExpression={handleOnRunPredictionFilterExpression}
                onValueClick={handleEventClick}
                findPredictionFromLabel={findPredictionFromLabel}
                findLabelFromPrediction={findLabelFromPrediction}
                selectRandomPrediction={selectRandomPrediction}
              />
            </Sidebar>
          </Split>
        </div>
        <div style={{position: 'relative'}}>
          <Timeline
            userCanAddRows
            userChannels={userChannels}
            setUserChannels={setUserChannels}
            // showLabels={canLabel}
            data={compundItem}
            duration={videoDuration}
            currentEvent={currentEvent}
            currentValue={currentTime}
            userLabels={currentUserLabels}
            channelLock={channelLock}
            channelTypeDescriptions={staticChannelTypeDescriptions}
            toggleChannelLock={toggleChannelLock}
            onValueClick={handleEventClick}
            onTimeDoubleClick={handleOnTimeDoubleClick}
            findPredictionFromLabel={findPredictionFromLabel}
            // findLabelFromPrediction={findLabelFromPrediction}
            actionsLeft={
              <>
                <VideoControls
                  videoRef={videoRef}
                  // onSeekBackDisabled={currentEvent === undefined}
                  // onSeekForwardDisabled={currentEvent === undefined}
                  onPrevious={catalogSidebarRef.current?.selectPreviousItem}
                  onNext={catalogSidebarRef.current?.selectNextItem}
                  onSeekBack={() =>
                    currentEvent ? selectPreviousPredictionOrLabel(currentEvent) : undefined
                  }
                  onSeekForward={() =>
                    currentEvent ? selectNextPredictionOrLabel(currentEvent) : undefined
                  }
                />
                <Popup
                  size='tiny'
                  content='video will resume playback when clip selection changes'
                  trigger={
                    <Checkbox
                      label='auto-play'
                      checked={autoPlay}
                      onChange={(_e, {checked}) => setAutoPlay(checked === true)}
                    />
                  }
                />
                <div>
                  rate:
                  <Popup
                    size='tiny'
                    content='select playback rate (comma/period)'
                    trigger={
                      <Dropdown
                        selection
                        compact
                        value={playbackRate}
                        options={_.map(supportedPlaybackRates, (v) => ({text: `${v}x`, value: v}))}
                        onChange={(_e, {value}) => setPlaybackRate(value as number)}
                      />
                    }
                  />
                </div>
              </>
            }
            actionsRight={
              <>
                {/* <Popup
                            size='tiny'
                            content='read-only (disables labeling)'
                            trigger={<Checkbox
                                label='r-o'
                                checked={isLabelingDisabled}
                                onChange={(_e, {checked}) => updateConfig({isLabelingDisabled: checked})}
                            />}
                        /> */}
                <Popup
                  size='tiny'
                  content='reformat times'
                  trigger={
                    <Checkbox
                      label='hh:mm:ss'
                      checked={formatTime}
                      onChange={(_e, {checked}) => updateConfig({formatTime: checked})}
                    />
                  }
                />
                <div className='buttons'>
                  {_.chain(playbackModes)
                    .toPairs()
                    .map(([k, v]: [keyof IPlaybackModeMap, IPlaybackMode]) => {
                      const {buttonContent, description} = v
                      return (
                        <Popup
                          key={k}
                          size='tiny'
                          content={description}
                          style={{
                            padding: '0.25em',
                          }}
                          trigger={
                            <button
                              onClick={() => togglePlaybackMode(k)}
                              onFocus={(e) => e.target.blur()}
                              color={_.includes(activePlaybackModes, k) ? 'blue' : undefined}
                              style={{
                                ...(_.includes(activePlaybackModes, k) ?
                                  {
                                    backgroundColor: '#ff099a',
                                    color: 'white',
                                  }
                                : undefined),
                              }}
                            >
                              {buttonContent}
                            </button>
                          }
                        />
                      )
                    })
                    .value()}
                </div>
                <div>
                  seek offset:
                  <Dropdown
                    selection
                    compact
                    value={seekOffset}
                    options={_.chain([_.range(0, 6), 10, 30, 60])
                      .flatten()
                      .map((i) => ({text: -i, value: -i}))
                      .value()}
                    onChange={(_e, {value}) => {
                      if (!_.isNumber(value)) return
                      setSeekOffset(value)
                    }}
                  />
                </div>
              </>
            }
          />
          <Dimmer active={isPredictionsLoading}>
            <Loader />
          </Dimmer>
        </div>
      </Split>
      <CommandModal
        ref={commandModalRef}
        currentTime={currentTime}
        onSeek={(t: number) => {
          if (!videoRef.current) return
          videoRef.current.currentTime = t
        }}
      />
    </div>
  )
}

function VideoLabLabelWrapper() {
  const {isLabelingDisabled} = useConfig()
  return (
    <LabelingContextWrapper disabled={isLabelingDisabled}>
      <RouteVideoLabInternal />
    </LabelingContextWrapper>
  )
}

export function RouteVideoLab() {
  return (
    <VideolabConfigWrapper>
      <VideoLabLabelWrapper />
    </VideolabConfigWrapper>
  )
}

export default RouteVideoLab
